import { documentApi } from '@/entities/Document';
import api from '@/shared/api';
import { DocumentRevisionStatus, DocumentRevisionStatusLabels } from '@/shared/lib';
import useRevisionFileDelivery, { type RevisionFileData } from './useRevisionFileDelivery';

interface DocumentData {
  revisions: RevisionFileData[];
}

export default function useDocuments() {
  const { error, isLoading, deliverRevisionFile, runFileDelivery } = useRevisionFileDelivery();
  // Views in flight, keyed by document+revision: a double click on the eye fired two document
  // lookups and, for non-PDF formats, two downloads of the same file.
  const viewsInFlight = new Set<string>();

  const getRevisions = async (
    documentId: string,
    documentData?: DocumentData
  ): Promise<RevisionFileData[]> => {
    if (documentData?.revisions) {
      return documentData.revisions;
    }
    const apiResponse = await api.get(`documents/${documentId}`);
    const revisions = apiResponse.data?.data?.revisions;

    return Array.isArray(revisions) ? revisions : [];
  };

  const findRevision = (
    revisions: RevisionFileData[],
    revisionId?: number,
    revisionNum?: number
  ): RevisionFileData | undefined => {
    // An explicit revision request gets no fallback. Falling through to the last revision would
    // open a file other than the one that was clicked, without warning.
    if (revisionId !== undefined) {
      return revisions.find((rev: { id: number }) => rev.id === revisionId);
    }

    if (revisionNum !== undefined) {
      return revisions[revisionNum] || revisions[revisions.length - 1];
    }

    return revisions.length > 0 ? revisions[revisions.length - 1] : undefined;
  };

  const getRevisionFileInfo = async (
    documentId: string,
    revisionId: number
  ): Promise<RevisionFileData | undefined> => {
    const revisions = await getRevisions(documentId);
    return findRevision(revisions, revisionId);
  };

  const registerView = async (documentId: string, revision: RevisionFileData) => {
    try {
      await documentApi.registerDocumentView(Number(documentId), revision.id);
    } catch {
      // best-effort: falha ao registrar auditoria não deve impactar a visualização
    }
  };

  const viewDocument = async (
    documentId: string,
    revisionNum?: number,
    revisionId?: number,
    documentData?: DocumentData
  ) => {
    const viewKey = `${documentId}:${revisionId ?? revisionNum ?? 'current'}`;

    if (viewsInFlight.has(viewKey)) {
      return;
    }

    viewsInFlight.add(viewKey);

    try {
      await runFileDelivery(async () => {
        const revisions = await getRevisions(documentId, documentData);
        const revision = findRevision(revisions, revisionId, revisionNum);

        await deliverRevisionFile(
          revision,
          (endpoint) => ({
            path: `/documents/${documentId}/${endpoint}`,
            // The resolved revision travels with the file request: without it the endpoints fall
            // back to the current revision and serve a file other than the one that was clicked.
            ...(revision ? { params: { revisionId: revision.id } } : {}),
          }),
          (viewedRevision) => registerView(documentId, viewedRevision)
        );
      });
    } finally {
      viewsInFlight.delete(viewKey);
    }
  };

  const formatStatus = (status: DocumentRevisionStatus | string) => {
    if (!status) return '';

    if (Object.values(DocumentRevisionStatus).includes(status as DocumentRevisionStatus)) {
      return DocumentRevisionStatusLabels[status as DocumentRevisionStatus];
    }

    // Fallback for any unknown status
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  return {
    viewDocument,
    getRevisionFileInfo,
    formatStatus,
    error,
    isLoading,
  };
}
